import React from "react";

import { IcStat, IcLine, IcArea, IcBar, IcDonut, IcTable, IcGauge, IcDotGrid, IcHeat, IcCompare } from "./icons";

/* Chart types a metric can be shown as. Icons are inline so they inherit color. */
export const CHART_TYPES = [
  { t: "stat", label: "Number", ic: <IcStat /> },
  { t: "line", label: "Line", ic: <IcLine /> },
  { t: "area", label: "Area", ic: <IcArea /> },
  { t: "bar", label: "Bars", ic: <IcBar /> },
  { t: "donut", label: "Progress", ic: <IcDonut /> },
  { t: "gauge", label: "Gauge", ic: <IcGauge /> },
  { t: "dots", label: "Dot grid", ic: <IcDotGrid /> },
  { t: "heatmap", label: "Heatmap", ic: <IcHeat /> },
  { t: "compare", label: "Compare", ic: <IcCompare /> },
  { t: "table", label: "Table", ic: <IcTable /> },
];

export const typeLabel = (t) => CHART_TYPES.find((x) => x.t === t)?.label || "Number";

// Sensible default size (grid units) per chart type.
export const defaultSize = (t) => {
  if (t === "stat" || t === "donut") return { w: 3, h: 3 };
  if (t === "gauge") return { w: 4, h: 6 };
  if (t === "table") return { w: 4, h: 5 };
  if (t === "compare") return { w: 8, h: 7 };
  if (t === "dots" || t === "heatmap") return { w: 6, h: 6 };
  return { w: 6, h: 5 }; // line / area / bar
};

// A reasonable default chart type when a metric is first dropped in.
export const defaultTypeForMetric = () => "area";

export const METRIC_ICON = <IcBar />;

/* Widget accent colors — soft, muted data-ink tones (cards stay white).
   "brand" (null) falls back to the company color. Auto-assigned in slot
   order when metrics land; changeable per card in Edit widget. */
export const ACCENTS = [
  { id: "brand", label: "Brand", c: null, c2: null, soft: null },
  { id: "green", label: "Green", c: "#1D9E75", c2: "#5DCAA5", soft: "rgba(29, 158, 117, 0.12)" },
  { id: "blue", label: "Blue", c: "#378ADD", c2: "#85B7EB", soft: "rgba(55, 138, 221, 0.12)" },
  { id: "violet", label: "Violet", c: "#7F77DD", c2: "#AFA9EC", soft: "rgba(127, 119, 221, 0.12)" },
  { id: "amber", label: "Amber", c: "#EF9F27", c2: "#FAC775", soft: "rgba(239, 159, 39, 0.14)" },
  { id: "coral", label: "Coral", c: "#D85A30", c2: "#F0997B", soft: "rgba(216, 90, 48, 0.12)" },
];

export const accentById = (id) => ACCENTS.find((a) => a.id === id) || ACCENTS[0];

// The rotation used when metrics land on the board (skips "brand" so a fresh
// template reads varied, like the reference design).
export const ACCENT_CYCLE = ["blue", "green", "amber", "coral"];

/* Build the widget list for a fresh dashboard: template slots filled in order
   by the picked metrics (extras stack below); no template = a simple stack. */
export const widgetsFromTemplate = (tpl, metricIds) => {
  const cycle = (i) => ({ accent: ACCENT_CYCLE[i % ACCENT_CYCLE.length] });
  if (!tpl) {
    return (metricIds || []).map((id, i) => {
      const type = defaultTypeForMetric();
      const size = defaultSize(type);
      return { metric: id, title: "", chartType: type, x: 0, y: i * size.h, ...size, options: cycle(i) };
    });
  }
  const widgets = [];
  tpl.slots.forEach((slot, i) => {
    if (!metricIds[i]) return;
    widgets.push({
      metric: metricIds[i],
      title: "",
      chartType: slot.t,
      x: slot.x,
      y: slot.y,
      w: slot.w,
      h: slot.h,
      options: cycle(i),
    });
  });
  const overflowY = tpl.slots.reduce((m, s) => Math.max(m, s.y + s.h), 0);
  (metricIds || []).slice(tpl.slots.length).forEach((id, i) => {
    const type = defaultTypeForMetric();
    const size = defaultSize(type);
    widgets.push({ metric: id, title: "", chartType: type, x: 0, y: overflowY + i * size.h, ...size, options: cycle(tpl.slots.length + i) });
  });
  return widgets;
};

/* ── Component (role) templates ──────────────────────────────────────────────
   The metric-layout TEMPLATES below are chart SLOTS filled by picked metrics.
   A COMPONENT template is different: it's a ready board of the SAME game
   components the builder already has — King of the Mountain, Beat the Yeti,
   a custom metric+payout-curve card, Milestone bonuses, Anticipated earnings —
   each pre-placed AND pre-configured (scene, payout curve, title), and scoped
   at creation to the workspaces named here, averaged. Nothing new is invented;
   picking it just drops the real components in, ready. No metric-picking step.

   `workspaceNames` → resolved to real ids at creation (DashboardsPanel), so the
   template works in any company with workspaces by those names. A `custom: true`
   entry becomes a fresh "Build your own" card; its metric is resolved to a
   revenue metric of the first scoped workspace when one is available. */
export const COMPONENT_TEMPLATES = [
  {
    id: "head-of-sales",
    name: "Head of Sales",
    desc: "A ready team scorecard — the mountain, the Yeti gates, a bonus payout curve, a milestone and projected earnings, all averaged across your sales teams.",
    workspaceNames: ["Sales", "In-House Sales"],
    components: [
      { comp: "kotm", x: 0, y: 0, w: 6, h: 8, options: { title: "Goal of the Mountain", scene: "photo" } },
      { comp: "beatyeti", x: 6, y: 0, w: 6, h: 8, options: { title: "Beat the Yeti", scene: "mountains" } },
      {
        custom: true, x: 0, y: 8, w: 6, h: 7,
        options: {
          title: "Quarterly Performance Bonus",
          payoutCurve: {
            on: true,
            /* The revenue ladder from the plan. The two accelerator tiers (110
               and 120) start gated on the four sales health checks; the admin
               binds each gate to a metric in the card's settings. */
            tiers: [
              { at: 90, unit: "%", pay: 4375 },
              { at: 95, unit: "%", pay: 8750 },
              { at: 100, unit: "%", pay: 12500 },
              {
                at: 110, unit: "%", pay: 15625, gate: true,
                gates: [
                  /* The plan's own four checks. Minimum unlocks the tier; target
                     is what they chase, left empty where the plan names none.
                     Membership discipline has no metric behind it, so a person
                     confirms it each quarter. */
                  { name: "Close rate", source: "metric", metric: "", metricName: "", unit: "%", min: 50, target: 55, period: "quarter", on: true },
                  { name: "Average ticket", source: "metric", metric: "", metricName: "", unit: "$", min: 1250, target: null, period: "quarter", on: true },
                  { name: "Quality bid accuracy", source: "metric", metric: "", metricName: "", unit: "%", min: 95, target: null, period: "year", on: true },
                  { name: "Membership discipline", source: "manual", metric: "", metricName: "", unit: "%", min: null, target: null, period: "quarter", on: true, confirmations: {} },
                ],
              },
              {
                at: 120, unit: "%", pay: 18750, gate: true,
                gates: [
                  /* The plan's own four checks. Minimum unlocks the tier; target
                     is what they chase, left empty where the plan names none.
                     Membership discipline has no metric behind it, so a person
                     confirms it each quarter. */
                  { name: "Close rate", source: "metric", metric: "", metricName: "", unit: "%", min: 50, target: 55, period: "quarter", on: true },
                  { name: "Average ticket", source: "metric", metric: "", metricName: "", unit: "$", min: 1250, target: null, period: "quarter", on: true },
                  { name: "Quality bid accuracy", source: "metric", metric: "", metricName: "", unit: "%", min: 95, target: null, period: "year", on: true },
                  { name: "Membership discipline", source: "manual", metric: "", metricName: "", unit: "%", min: null, target: null, period: "quarter", on: true, confirmations: {} },
                ],
              },
            ],
          },
        },
      },
      { comp: "milestones", x: 6, y: 8, w: 6, h: 7, options: { title: "Q4 Milestone", scene: "steps" } },
      { comp: "earnings", x: 0, y: 15, w: 12, h: 7, options: { title: "Total Anticipated Earnings", scene: "ledger" } },
    ],
  },
];

/* Dashboard layout templates. Each slot is a grid position (12-col, rowHeight
   units) plus the chart type that shape reads best as. Picking a template and
   a set of metrics fills the slots in order; extras append below. */
export const TEMPLATES = [
  {
    id: "overview",
    name: "Overview",
    desc: "Four numbers up top, one big trend",
    slots: [
      { x: 0, y: 0, w: 3, h: 3, t: "stat" },
      { x: 3, y: 0, w: 3, h: 3, t: "stat" },
      { x: 6, y: 0, w: 3, h: 3, t: "stat" },
      { x: 9, y: 0, w: 3, h: 3, t: "stat" },
      { x: 0, y: 3, w: 8, h: 7, t: "area" },
      { x: 8, y: 3, w: 4, h: 7, t: "bar" },
    ],
  },
  {
    id: "trends",
    name: "Trends",
    desc: "Two charts side by side",
    slots: [
      { x: 0, y: 0, w: 6, h: 6, t: "area" },
      { x: 6, y: 0, w: 6, h: 6, t: "line" },
    ],
  },
  {
    id: "kpiwall",
    name: "KPI wall",
    desc: "A grid of key numbers",
    slots: [
      { x: 0, y: 0, w: 3, h: 3, t: "stat" },
      { x: 3, y: 0, w: 3, h: 3, t: "stat" },
      { x: 6, y: 0, w: 3, h: 3, t: "stat" },
      { x: 9, y: 0, w: 3, h: 3, t: "stat" },
      { x: 0, y: 3, w: 3, h: 3, t: "stat" },
      { x: 3, y: 3, w: 3, h: 3, t: "stat" },
      { x: 6, y: 3, w: 3, h: 3, t: "stat" },
      { x: 9, y: 3, w: 3, h: 3, t: "stat" },
    ],
  },
  {
    id: "report",
    name: "Report",
    desc: "A trend beside the raw numbers",
    slots: [
      { x: 0, y: 0, w: 7, h: 6, t: "area" },
      { x: 7, y: 0, w: 5, h: 6, t: "table" },
    ],
  },
  {
    id: "focus",
    name: "Focus",
    desc: "One hero chart, three numbers",
    slots: [
      { x: 0, y: 0, w: 8, h: 9, t: "area" },
      { x: 8, y: 0, w: 4, h: 3, t: "stat" },
      { x: 8, y: 3, w: 4, h: 3, t: "stat" },
      { x: 8, y: 6, w: 4, h: 3, t: "stat" },
    ],
  },
  {
    id: "insights",
    name: "Insights",
    desc: "Numbers, a comparison band, distribution",
    slots: [
      { x: 0, y: 0, w: 4, h: 3, t: "stat" },
      { x: 4, y: 0, w: 4, h: 3, t: "stat" },
      { x: 8, y: 0, w: 4, h: 3, t: "stat" },
      { x: 0, y: 3, w: 12, h: 7, t: "compare" },
      { x: 0, y: 10, w: 7, h: 6, t: "bar" },
      { x: 7, y: 10, w: 5, h: 6, t: "donut" },
    ],
  },
  {
    id: "weekly",
    name: "Weekly report",
    desc: "Score gauge, dot grid and a heatmap",
    slots: [
      { x: 0, y: 0, w: 3, h: 3, t: "stat" },
      { x: 3, y: 0, w: 3, h: 3, t: "stat" },
      { x: 6, y: 0, w: 3, h: 3, t: "stat" },
      { x: 9, y: 0, w: 3, h: 3, t: "stat" },
      { x: 0, y: 3, w: 4, h: 6, t: "gauge" },
      { x: 4, y: 3, w: 8, h: 6, t: "dots" },
      { x: 0, y: 9, w: 7, h: 6, t: "heatmap" },
      { x: 7, y: 9, w: 5, h: 6, t: "donut" },
    ],
  },
];
